import { FC, useCallback, useEffect } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, Button, Svg, useThemeValue } from '@altermaind/web/ui';
import { RenderLocalesWithComponents } from '@/_shared/components';
import { convertColorToRgba } from '@/_shared/helpers/convertColorToRgba';
import { openTabForAsyncRedirect } from '@/_shared/helpers/openTabForAsyncRedirect';
import { useDialog } from '@/_shared/hooks';
import { useLocalizedFullDate } from '@/_shared/hooks/useLocalizedDateName';
import type { DialogContactsPayload } from '@/core/components/DialogContacts/types';
import { getUserWarningsCall } from '@/core/state/api/getUserWarningsCall';
import { getBillingLinkCall } from '../../state';

export const DialogTrialExpiring: FC = () => {
  const t = useTranslations('billing.dialogTrialExpiring');
  const { closeDialog, Dialog, isOpen, openDialog } = useDialog<{ expiryDate: string }>(
    'trialExpiring',
  );
  const { openDialog: openContacts } = useDialog<DialogContactsPayload>('contacts');
  const { isTrialExpiring, isDialogTrialExpiringDismissed, dismissDialogTrialExpiring } =
    getUserWarningsCall.hook();
  const warningBg = useThemeValue('colors', 'background/warning');
  const { stringToLocalizedFullDate } = useLocalizedFullDate();
  const { execute: executeGetBillingLink } = getBillingLinkCall.hook();

  const handleClose = () => {
    dismissDialogTrialExpiring({});
    closeDialog();
  };

  const handleAddPaymentMethod = useCallback(() => {
    const { redirect, close } = openTabForAsyncRedirect();

    executeGetBillingLink({
      successSaga: ({ response }) => {
        if (response?.link) {
          redirect(response.link);
        } else {
          close();
        }
      },
      errorSaga: () => {
        close();
      },
    });
  }, [executeGetBillingLink]);

  useEffect(() => {
    if (isTrialExpiring?.hasShowDialog && !isDialogTrialExpiringDismissed) {
      openDialog();
    }
  }, [isTrialExpiring?.hasShowDialog, isDialogTrialExpiringDismissed, openDialog]);

  return (
    <Dialog maxWidth='800px' open={isOpen} title={undefined} onClose={handleClose} closeButton>
      <Box
        display='flex'
        mb='m'
        flexDirection='column'
        alignItems='center'
        bg={warningBg ? (convertColorToRgba(warningBg, 0.1) as never) : 'background/warning'}
        p='xs'
        borderRadius='100%'
      >
        <Svg name='warning' size='xxl' stroke='warning' />
      </Box>
      <Box display='flex' flexDirection='column'>
        <Typography mt='none' variant='h3' color='primary/text' textAlign='center'>
          {t('title')}
        </Typography>
      </Box>
      <Box mb='xs' mt='xs' width='100%'>
        <Typography variant='p2/light' textAlign='center' fontWeight={200} color='grey/light'>
          <RenderLocalesWithComponents
            text={
              isTrialExpiring?.type === 'warning' ? t('descriptionWarning') : t('descriptionInfo')
            }
            placeholders={{
              '!{BOLD}!': (
                <Typography as='span' variant='p2/bold'>
                  {t(
                    isTrialExpiring?.type === 'warning'
                      ? 'descriptionWarningBold'
                      : 'descriptionInfoBold',
                    {
                      deltaOfDays: isTrialExpiring?.deltaOfDays || 0,
                      expiringDate: stringToLocalizedFullDate(isTrialExpiring?.expiringDate),
                    },
                  )}
                </Typography>
              ),
            }}
          />
        </Typography>
      </Box>
      <Box mt='l' display='flex' flexDirection='row' justifyContent='center'>
        <Button variant='primary' onClick={handleAddPaymentMethod}>
          {t('addPaymentMethod')}
        </Button>
      </Box>
      <Box display='flex' flexDirection='row' justifyContent='center'>
        <Button variant='link/primary' onClick={() => openContacts({})}>
          {t('contactUs')}
        </Button>
      </Box>
    </Dialog>
  );
};
